import React, { useState, useEffect, useRef } from 'react';
import { Target, Eye, Award, ArrowRight } from 'lucide-react';
import AnimatedCounter from '../ui/AnimatedCounter';

const MissionVisionSection = () => {
  const [activeTab, setActiveTab] = useState('mission');
  const sectionRef = useRef(null);

  const content = {
    mission: {
      title: 'Our Mission',
      description: 'At AMTB Solution, our mission is to empower businesses with cutting-edge technology solutions that drive growth and innovation. We deliver scalable, secure, and efficient IT services that help organizations automate operations, improve productivity, and achieve their digital transformation goals.',
      icon: Target,
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500'
    },
    vision: {
      title: 'Our Vision',
      description: 'To be the most trusted IT partner for startups and enterprises worldwide, leading the way in innovative software development, cloud solutions, and digital transformation. We envision a future where every business, regardless of size, has access to world-class technology solutions.',
      icon: Eye,
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=500'
    },
    experience: {
      title: 'Our Experience',
      description: 'With over a decade of experience in IT services and software development, we have successfully delivered 100+ projects across diverse industries. Our team of skilled professionals brings practical expertise and measurable outcomes to every engagement.',
      icon: Award,
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=500'
    }
  };

  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '100+', label: 'Projects Delivered' },
    { value: '50+', label: 'Happy Clients' },
    { value: '24/7', label: 'Support Available' }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23B8336A' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
            Our Foundation
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Building Secure Digital Futures Through
            <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Innovation</span>
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          {Object.keys(content).map((key) => {
            const Icon = content[key].icon;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white shadow-lg shadow-[#B8336A]/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon size={18} />
                <span className="font-medium capitalize">{key}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={content[activeTab].image}
                alt={content[activeTab].title}
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-2xl p-6 shadow-xl">
              <div className="text-3xl font-bold">10+</div>
              <div className="text-sm opacity-90">Years of Excellence</div>
            </div>
          </div>

          {/* Text Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2">
              {(() => {
                const Icon = content[activeTab].icon;
                return (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 flex items-center justify-center">
                    <Icon size={24} className="text-[#B8336A]" />
                  </div>
                );
              })()}
            </div>
            
            <h3 className="text-3xl font-bold text-gray-900">
              {content[activeTab].title}
            </h3>
            
            <p className="text-gray-600 text-lg leading-relaxed">
              {content[activeTab].description}
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4">
                  <div className="text-2xl font-bold bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent">
                    <AnimatedCounter end={stat.value} suffix={stat.value.includes('+') ? '+' : ''} />
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVisionSection;
